import { isAxiosError, type AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hook_au/useAuth';
import apiClient from './apiClient';
import {
  normalizeOrdersResponse,
  type OrderEmailPayload,
  type Order,
  type OrderCreatePayload,
  type OrderFilters,
  type OrderPagination,
  type OrderUpdatePayload,
} from '../types/orders';

type RequestConfig<Response> = AxiosRequestConfig & { skipAuth?: boolean };

const PAGE_SIZES = [5, 10, 50] as const;
const DEFAULT_PAGE_SIZE = 10;
const normalizePageSize = (size: number) => (PAGE_SIZES.includes(size as (typeof PAGE_SIZES)[number]) ? size : DEFAULT_PAGE_SIZE);

const ERROR_MESSAGES = {
  unauthorized: 'La sesión expiró. Vuelve a iniciar sesión para continuar.',
  notFound: 'No encontramos el pedido solicitado.',
  unknown: 'No pudimos conectar con el servicio de pedidos. Inténtalo de nuevo en unos segundos.',
  list: 'No pudimos cargar los pedidos. Inténtalo de nuevo en unos segundos.',
} as const;

const normalizeFilters = (raw: OrderFilters): OrderFilters => ({
  search: raw.search?.trim() || undefined,
  status: raw.status,
  supplier: raw.supplier,
  project: raw.project,
  subproject: raw.subproject,
  supplier_called: raw.supplier_called,
});

const sameFilters = (a: OrderFilters, b: OrderFilters) =>
  ['search', 'status', 'supplier', 'project', 'subproject', 'supplier_called'].every(
    (key) => a[key as keyof OrderFilters] === b[key as keyof OrderFilters],
  );

export function useOrdersApi(
  initialFilters: OrderFilters = {},
  initialPagination: OrderPagination = { page: 1, page_size: DEFAULT_PAGE_SIZE },
) {
  const { token } = useAuth();
  const [filters, setFilters] = useState<OrderFilters>(initialFilters);
  const [pagination, setPagination] = useState<OrderPagination>({
    ...initialPagination,
    page_size: normalizePageSize(initialPagination.page_size ?? DEFAULT_PAGE_SIZE),
    total: initialPagination.total ?? 0,
  });
  const [data, setData] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) {
        throw new Error('No hay sesión activa para llamar a la API de pedidos');
      }
      try {
        const response = await apiClient.request<Response>({
          ...config,
          url: `/orders${config.url ?? ''}`,
        });
        return response.data;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 401) {
          throw new Error(ERROR_MESSAGES.unauthorized);
        }
        throw err;
      }
    },
    [token],
  );

  const fetchOrders = useCallback(
    async (overrides: { filters?: OrderFilters; page?: number; page_size?: number } = {}) => {
      setLoading(true);
      setError(null);

      const normalizedFilters = normalizeFilters({ ...filters, ...(overrides.filters ?? {}) });
      const filtersChanged = !sameFilters(filters, normalizedFilters);
      const nextPageSize = normalizePageSize(overrides.page_size ?? pagination.page_size);
      const nextPage = overrides.page ?? (filtersChanged ? 1 : pagination.page);
      const offset = (nextPage - 1) * nextPageSize;

      try {
        const resp = await request<any>({
          method: 'GET',
          url: '',
          params: {
            search: normalizedFilters.search,
            status: normalizedFilters.status,
            supplier_id: normalizedFilters.supplier,
            project_id: normalizedFilters.project,
            subproject_id: normalizedFilters.subproject,
            supplier_called: normalizedFilters.supplier_called,
            limit: nextPageSize,
            offset,
          },
        });
        const normalized = normalizeOrdersResponse(resp);
        const computedPageSize = normalizePageSize(normalized.pagination.page_size ?? nextPageSize);
        const computedTotal = normalized.pagination.total ?? pagination.total ?? offset + normalized.items.length;

        setData(normalized.items);
        setFilters((prev) => (sameFilters(prev, normalizedFilters) ? prev : normalizedFilters));
        setPagination((prev) => {
          const next = { ...prev, ...normalized.pagination, total: computedTotal, page: nextPage, page_size: computedPageSize };
          return (
            prev.page === next.page &&
            prev.page_size === next.page_size &&
            prev.total === next.total &&
            prev.total_pages === next.total_pages
          )
            ? prev
            : next;
        });

        return normalized;
      } catch (err) {
        const message = isAxiosError(err) && err.response?.status === 401 ? ERROR_MESSAGES.unauthorized : ERROR_MESSAGES.list;
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [request, filters, pagination.page, pagination.page_size],
  );

  useEffect(() => {
    if (!token) return;
    void fetchOrders({ filters, page: pagination.page, page_size: pagination.page_size });
  }, [token, filters, pagination.page, pagination.page_size, fetchOrders]);

  const goToPage = (page: number) => fetchOrders({ page });
  const refresh = () => fetchOrders();

  return { data, loading, error, pagination, filters, setFilters, setPagination, goToPage, refresh };
}

export function useOrderActions() {
  const { token } = useAuth();

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) {
        throw new Error('No hay sesión activa para llamar a la API de pedidos');
      }
      try {
        const response = await apiClient.request<Response>({
          ...config,
          url: `/orders${config.url ?? ''}`,
        });
        return response.data;
      } catch (err) {
        if (isAxiosError(err)) {
          const status = err.response?.status;
          if (status === 401) throw new Error(ERROR_MESSAGES.unauthorized);
          if (status === 404) throw new Error(ERROR_MESSAGES.notFound);
        }
        throw new Error(ERROR_MESSAGES.unknown);
      }
    },
    [token],
  );

  const createOrder = useCallback(
    (payload: OrderCreatePayload) =>
      request<Order>({ method: 'POST', url: '', data: payload }),
    [request],
  );

  const downloadOrderPdf = useCallback(
    (id: Order['id']) =>
      request<Blob>({ method: 'GET', url: `/${id}/pdf`, responseType: 'blob' }),
    [request],
  );

  const updateOrder = useCallback(
    (id: Order['id'], payload: OrderUpdatePayload) =>
      request<Order>({ method: 'PUT', url: `/${id}`, data: payload }),
    [request],
  );

  const sendOrderEmail = useCallback(
    (id: Order['id'], payload: OrderEmailPayload) => {
      const formData = new FormData();
      formData.append('to_email', payload.to_email);
      formData.append('subject', payload.subject);
      formData.append('body', payload.body);
      payload.extra_attachments?.forEach((file) => {
        formData.append('extra_attachments', file);
      });
      return request<Order>({
        method: 'POST',
        url: `/${id}/send-email`,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    [request],
  );

  const deleteOrder = useCallback(
    (id: Order['id']) =>
      request<void>({ method: 'DELETE', url: `/${id}` }),
    [request],
  );

  return { createOrder, updateOrder, deleteOrder, downloadOrderPdf, sendOrderEmail };
}
